/**
 * Maps a slideshow's sound-timeline segments to absolute playback windows so audio can be
 * synced to the slide timeline's playhead.
 *
 * Each segment's `slideshowSegmentStart` is an absolute millisecond timestamp on the slideshow
 * timeline. The song then occupies
 * `[slideshowSegmentStart, slideshowSegmentStart + (endMs - startMs))`, where the length is the
 * trimmed duration (`startMs`/`endMs` are the trim-in/trim-out points into the song's own
 * audio, editable via the sound row's trim handles). Segments may leave silent gaps between
 * them.
 */

export interface ISongPlaybackWindow {
    songId: string;
    /** Inclusive start of this song on the slideshow timeline (ms). */
    timelineStartMs: number;
    /** Exclusive end of this song on the slideshow timeline (ms). */
    timelineEndMs: number;
    /** Offset into the song where playback begins (ms) — the trim start. */
    trimStartMs: number;
    /** Pre-resolved stream URL embedded on the segment, if any — see ISlideshowSongSegment. */
    streamUrl?: string | null;
}

export type SongSegmentInput = Pick<
    SpApi.ISlideshowSongSegment,
    'songId' | 'slideshowSegmentStart' | 'startMs' | 'endMs' | 'streamUrl'
>;

export function getSegmentTrimDurationMs(segment: SongSegmentInput): number {
    const trimStartMs = Math.max(0, segment.startMs ?? 0);
    const trimEndMs = Math.max(trimStartMs, segment.endMs ?? 0);

    return trimEndMs - trimStartMs;
}

/**
 * Builds absolute playback windows from sound-timeline segments.
 * Segments without a positive trimmed duration or a song id are skipped. Windows are sorted by
 * timeline start so lookups stay deterministic when segments are stored out of order.
 */
export function buildSongPlaybackWindows(segments: SongSegmentInput[]): ISongPlaybackWindow[] {
    const windows: ISongPlaybackWindow[] = [];

    segments.forEach((segment) => {
        const durationMs = getSegmentTrimDurationMs(segment);

        if (!segment.songId || durationMs <= 0) {
            return;
        }

        const timelineStartMs = Math.max(0, segment.slideshowSegmentStart ?? 0);

        windows.push({
            songId: String(segment.songId),
            timelineStartMs,
            timelineEndMs: timelineStartMs + durationMs,
            trimStartMs: Math.max(0, segment.startMs ?? 0),
            streamUrl: segment.streamUrl ?? null
        });
    });

    return windows.sort((a, b) => a.timelineStartMs - b.timelineStartMs);
}

/**
 * Returns the playback window active at the given timeline position, or null when the playhead
 * sits before the first song, in a gap, or past the last song.
 */
export function findActiveSongWindow(
    windows: ISongPlaybackWindow[],
    timelineMs: number
): ISongPlaybackWindow | null {
    return (
        windows.find(
            (window) => timelineMs >= window.timelineStartMs && timelineMs < window.timelineEndMs
        ) ?? null
    );
}

/**
 * The position (seconds) the song's audio element should be at for a given timeline position,
 * i.e. the trim start plus how far the playhead has advanced into this song's window.
 */
export function getSongPlaybackOffsetSeconds(
    window: ISongPlaybackWindow,
    timelineMs: number
): number {
    return (window.trimStartMs + (timelineMs - window.timelineStartMs)) / 1000;
}

/**
 * End of the soundtrack on the slideshow timeline (ms) — the latest segment end, accounting for
 * each segment's absolute `slideshowSegmentStart`.
 */
export function getSongSegmentsDurationMs(segments: SongSegmentInput[] | null | undefined): number {
    const windows = buildSongPlaybackWindows(segments ?? []);

    if (windows.length === 0) {
        return 0;
    }

    return windows.reduce((maxEnd, window) => Math.max(maxEnd, window.timelineEndMs), 0);
}

/**
 * Next free timestamp for appending a song to the sound row: 0 when empty, otherwise the end of
 * the latest segment so newly added songs still abut the previous one by default.
 */
export function getNextSongSegmentStartMs(segments: SongSegmentInput[] | null | undefined): number {
    return getSongSegmentsDurationMs(segments);
}

/**
 * Repositions the sound-timeline segment at `segmentIndex` so it starts at (about)
 * `desiredStartMs`, letting a song be slid freely along the timeline and dropped into empty
 * space. Every other segment keeps its absolute start, so silent gaps are preserved. The
 * dragged song's start is clamped between its neighbours so songs never overlap; once its
 * centre passes a neighbour's centre it moves to that neighbour's other side (the "swap past
 * the middle" behaviour). Dropping into a fully packed run (no gap) abuts it after the
 * preceding song and nudges only the songs it would otherwise overlap rightwards. An
 * out-of-range index returns the array unchanged.
 */
export function positionSongSegment<T extends SongSegmentInput>(
    segments: T[],
    segmentIndex: number,
    desiredStartMs: number
): T[] {
    if (segmentIndex < 0 || segmentIndex >= segments.length) {
        return segments;
    }

    const draggedDurationMs = getSegmentTrimDurationMs(segments[segmentIndex]);
    const desiredStart = Math.max(0, Math.round(desiredStartMs));
    const draggedCenter = desiredStart + draggedDurationMs / 2;

    const others = segments
        .map((segment, index) => {
            const start = Math.max(0, segment.slideshowSegmentStart ?? 0);
            const duration = getSegmentTrimDurationMs(segment);

            return { index, start, end: start + duration, center: start + duration / 2 };
        })
        .filter((entry) => entry.index !== segmentIndex)
        .sort((a, b) => a.start - b.start);

    const previous = [...others].reverse().find((entry) => entry.center <= draggedCenter) ?? null;
    const next = others.find((entry) => entry.center > draggedCenter) ?? null;

    const lowerBoundMs = previous ? previous.end : 0;
    const upperBoundMs = next ? next.start - draggedDurationMs : Number.POSITIVE_INFINITY;
    const finalStart =
        upperBoundMs < lowerBoundMs
            ? lowerBoundMs
            : Math.min(Math.max(desiredStart, lowerBoundMs), upperBoundMs);

    const result = segments.map((segment, index) =>
        index === segmentIndex ? { ...segment, slideshowSegmentStart: finalStart } : segment
    );

    // Songs left of the drop can't be overlapped (finalStart is clamped to the previous song's
    // end), so only walk the ones to its right and push the few that the drop lands on top of;
    // a real gap absorbs the shift and stops the cascade.
    let cursorEndMs = finalStart + draggedDurationMs;

    for (const entry of others) {
        if (entry.end <= finalStart) {
            continue;
        }

        if (entry.start >= cursorEndMs) {
            cursorEndMs = entry.end;
            continue;
        }

        const durationMs = entry.end - entry.start;

        result[entry.index] = { ...result[entry.index], slideshowSegmentStart: cursorEndMs };
        cursorEndMs += durationMs;
    }

    return result;
}

export interface ISongSegmentNeighborBounds {
    /** Right edge (ms) of the nearest segment to the left — how far left this one may grow. 0 if none. */
    leftBoundMs: number;
    /**
     * Left edge (ms) of the nearest segment to the right — how far right this one may grow.
     * `Number.POSITIVE_INFINITY` when there's no segment to the right.
     */
    rightBoundMs: number;
}

/**
 * How far the segment at `index` may grow on each side before it would overlap a neighbour, given
 * every segment's current position and trimmed length. Trimming clamps to these bounds (segments
 * never overlap and are never auto-pushed — the user moves a neighbour to make room), mirroring
 * the no-overlap rule that `positionSongSegment` enforces for repositioning.
 */
export function getSongSegmentNeighborBounds(
    segments: SongSegmentInput[],
    index: number
): ISongSegmentNeighborBounds {
    const target = segments[index];

    if (!target) {
        return { leftBoundMs: 0, rightBoundMs: Number.POSITIVE_INFINITY };
    }

    const start = Math.max(0, target.slideshowSegmentStart ?? 0);
    const end = start + getSegmentTrimDurationMs(target);

    let leftBoundMs = 0;
    let rightBoundMs = Number.POSITIVE_INFINITY;

    segments.forEach((segment, segmentIndex) => {
        if (segmentIndex === index) {
            return;
        }

        const otherStart = Math.max(0, segment.slideshowSegmentStart ?? 0);
        const otherEnd = otherStart + getSegmentTrimDurationMs(segment);

        if (otherEnd <= start) {
            leftBoundMs = Math.max(leftBoundMs, otherEnd);
        } else if (otherStart >= end) {
            rightBoundMs = Math.min(rightBoundMs, otherStart);
        }
    });

    return { leftBoundMs, rightBoundMs };
}

/**
 * Playable length of a slideshow: long enough to cover both the slide content and the
 * soundtrack. When music runs past the last slide, playback continues with an empty frame
 * (see SlideshowPlayer) until the music ends.
 */
export function getSlideshowPlaybackDurationMs(
    slideContentDurationMs: number,
    songSegments: SongSegmentInput[] | null | undefined
): number {
    return Math.max(slideContentDurationMs, getSongSegmentsDurationMs(songSegments));
}

export interface ICascadeOverlapItem {
    startMs: number;
    durationMs: number;
}

export interface ICascadeResolution {
    /** The new item's resolved start — see `resolveCascadedStarts` for when this snaps forward. */
    newItemStartMs: number;
    /** Resolved starts for each existing item, in the same order as `existingItems`. */
    existingStartsMs: number[];
}

/**
 * Core of the "insert and push" behavior shared by `pushOverlappingSongSegments` (applied on
 * drop) and the sound lane's live drag preview (applied per animation frame while dragging).
 *
 * `overlapProbeDurationMs` is the on-screen width of the drag card, not the new item's own
 * duration — it decides whether the drop counts as touching one existing item or two. Touching
 * two at once snaps the new item's start forward to sit right after the first of them, inserting
 * between them instead of displacing the first. Touching only one never snaps — the new item
 * keeps its requested start, and that item (using the new item's real duration, and cascading
 * into anything after it) is pushed forward to clear it instead.
 */
export function resolveCascadedStarts(
    existingItems: readonly ICascadeOverlapItem[],
    newItem: ICascadeOverlapItem,
    overlapProbeDurationMs: number
): ICascadeResolution {
    const requestedStart = Math.max(0, newItem.startMs);
    const probeEnd = requestedStart + Math.max(0, overlapProbeDurationMs);

    // Existing items don't overlap each other, so this is naturally free of duplicates.
    const overlapping = existingItems
        .map((item) => ({ start: Math.max(0, item.startMs), durationMs: item.durationMs }))
        .filter(
            ({ start, durationMs }) =>
                start < probeEnd && start + Math.max(0, durationMs) > requestedStart
        )
        .sort((a, b) => a.start - b.start);

    const newStart =
        overlapping.length >= 2
            ? Math.max(
                  requestedStart,
                  overlapping[0].start + Math.max(0, overlapping[0].durationMs)
              )
            : requestedStart;

    const newEnd = newStart + Math.max(0, newItem.durationMs);

    // Items ending at or before the new one's (possibly snapped) start are left alone; the rest
    // are swept forward, in timeline order, starting right after it.
    const toSweep = existingItems
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => {
            const start = Math.max(0, item.startMs);

            return start + Math.max(0, item.durationMs) > newStart;
        })
        .sort((a, b) => Math.max(0, a.item.startMs) - Math.max(0, b.item.startMs));

    const existingStartsMs = existingItems.map((item) => Math.max(0, item.startMs));
    let cursor = newEnd;

    toSweep.forEach(({ item, index }) => {
        const start = Math.max(existingStartsMs[index], cursor);

        existingStartsMs[index] = start;
        cursor = start + Math.max(0, item.durationMs);
    });

    return { newItemStartMs: newStart, existingStartsMs };
}

/**
 * Inserts `newSegment` and pushes forward any segment it now overlaps (cascading through
 * subsequent segments) so the sound row never ends up with two songs occupying the same time.
 * `overlapProbeDurationMs` should be the same value the live drag preview used — see
 * `resolveCascadedStarts` — so the committed drop matches what was previewed.
 *
 * Only `slideshowSegmentStart` is changed on the segments that need to move — array order and
 * every other field (including saved `id`s) are preserved so persistence stays stable.
 */
export function pushOverlappingSongSegments<T extends SongSegmentInput>(
    existingSegments: readonly T[],
    newSegment: T,
    overlapProbeDurationMs: number
): T[] {
    const { newItemStartMs, existingStartsMs } = resolveCascadedStarts(
        existingSegments.map((segment) => ({
            startMs: Math.max(0, segment.slideshowSegmentStart ?? 0),
            durationMs: getSegmentTrimDurationMs(segment)
        })),
        {
            startMs: Math.max(0, newSegment.slideshowSegmentStart ?? 0),
            durationMs: getSegmentTrimDurationMs(newSegment)
        },
        overlapProbeDurationMs
    );

    const result = existingSegments.map((segment, index) => {
        const originalStart = Math.max(0, segment.slideshowSegmentStart ?? 0);
        const resolvedStart = existingStartsMs[index];

        return resolvedStart === originalStart
            ? segment
            : { ...segment, slideshowSegmentStart: resolvedStart };
    });

    result.push({ ...newSegment, slideshowSegmentStart: newItemStartMs });

    return result;
}
